import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, MapPin, ShoppingBag, Compass, Music, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Cross-domain global search: marketplace listings, travel places, music albums. */
export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ listings: [], places: [], albums: [] });
  const ref = useRef(null);
  const inputRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced search
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults({ listings: [], places: [], albums: [] });
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const [listingsRes, placesRes, albumsRes] = await Promise.all([
          axios.get(`${API}/listings?search=${encodeURIComponent(term)}&limit=5`).catch(() => ({ data: [] })),
          axios.get(`${API}/travel/places?q=${encodeURIComponent(term)}&limit=5`).catch(() => ({ data: [] })),
          axios.get(`${API}/music/albums?q=${encodeURIComponent(term)}&limit=4`).catch(() => ({ data: [] })),
        ]);
        setResults({
          listings: Array.isArray(listingsRes.data) ? listingsRes.data : (listingsRes.data?.items || []),
          places: placesRes.data || [],
          albums: albumsRes.data || [],
        });
      } catch {
        setResults({ listings: [], places: [], albums: [] });
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [q]);

  const totalResults = results.listings.length + results.places.length + results.albums.length;
  const showResults = open && q.trim().length >= 2;

  return (
    <div ref={ref} className="hidden md:flex flex-1 max-w-md mx-4 lg:mx-8 relative">
      <label className="w-full relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid pointer-events-none" />
        <input
          ref={inputRef}
          data-testid="topbar-search"
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar destinos, productos, música…"
          className="w-full bg-white/70 border hairline rounded-full pl-11 pr-9 py-2.5 text-sm placeholder:text-ink-mid outline-none focus:border-coral/60 focus:bg-white transition"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); inputRef.current?.focus(); }}
            data-testid="topbar-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full grid place-items-center hover:bg-cream-deep"
            aria-label="Limpiar"
          >
            <X className="w-3 h-3 text-ink-mid" />
          </button>
        )}
      </label>

      {showResults && (
        <div
          data-testid="topbar-search-results"
          className="absolute top-full left-0 right-0 mt-2 bg-cream-card border hairline rounded-2xl shadow-2xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
        >
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-ink-mid inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-4 h-4 animate-spin" /> Buscando…
            </div>
          )}
          {!loading && totalResults === 0 && (
            <p className="px-4 py-6 text-center text-sm text-ink-mid">
              Sin resultados para "<span className="font-medium">{q}</span>".
            </p>
          )}

          {!loading && results.places.length > 0 && (
            <Section icon={Compass} title="Destinos">
              {results.places.map((p) => (
                <ResultRow
                  key={`p-${p.id}`}
                  testid={`search-place-${p.id}`}
                  to={`/viajes`}
                  onClick={() => setOpen(false)}
                  thumb={p.image}
                  title={p.name}
                  subtitle={`${p.city ? p.city + ", " : ""}${p.country}`}
                  meta={p.category}
                />
              ))}
            </Section>
          )}

          {!loading && results.listings.length > 0 && (
            <Section icon={ShoppingBag} title="Marketplace">
              {results.listings.map((l) => (
                <ResultRow
                  key={`l-${l.id}`}
                  testid={`search-listing-${l.id}`}
                  to={`/marketplace`}
                  onClick={() => setOpen(false)}
                  thumb={(l.photos || [])[0]}
                  title={l.title}
                  subtitle={l.location || l.category}
                  meta={l.price_usd ? `$${l.price_usd}` : ""}
                />
              ))}
            </Section>
          )}

          {!loading && results.albums.length > 0 && (
            <Section icon={Music} title="Música">
              {results.albums.map((a) => (
                <ResultRow
                  key={`a-${a.id}`}
                  testid={`search-album-${a.id}`}
                  to={`/musica`}
                  onClick={() => setOpen(false)}
                  thumb={a.cover_url}
                  title={a.title}
                  subtitle={a.artist}
                  meta={a.genre}
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="border-b hairline last:border-b-0">
      <header className="px-4 py-2 bg-cream-deep/40 inline-flex items-center gap-1.5 w-full">
        <Icon className="w-3 h-3 text-coral" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-mid">{title}</span>
      </header>
      {children}
    </section>
  );
}

function ResultRow({ to, onClick, thumb, title, subtitle, meta, testid }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-3 px-4 py-2.5 hover:bg-cream-deep transition"
    >
      <div className="w-10 h-10 rounded-lg bg-cream-deep overflow-hidden shrink-0 grid place-items-center">
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <MapPin className="w-4 h-4 text-ink-mid" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{title}</p>
        <p className="text-[11px] text-ink-mid truncate font-mono">{subtitle}</p>
      </div>
      {meta && <span className="text-[11px] text-coral font-mono shrink-0">{meta}</span>}
    </Link>
  );
}
